import { WebLayout } from 'components/Layout';
import { motion } from 'framer-motion';
import HeaderBackgroundImg from 'assets/workshop.png';
import { PenTool, Package, Clock } from 'react-feather';
import { WrenchIcon } from 'lucide-react';
import { useServiceData } from '../api/getServiceData';

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
};

const stagger = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.25, delayChildren: 0.2 },
  },
};

export function Services() {
  const { data: serviceData, isLoading: serviceDataLoading } = useServiceData();

  return (
    <WebLayout>
      {/* ===== Hero Section ===== */}
      <section
        className="relative h-[60vh] flex items-center justify-center bg-center bg-cover text-white"
        style={{ backgroundImage: `url(${serviceData?.data?.header?.backgroundImage})` }}
      >
        <div className="absolute inset-0 bg-black/60"></div>
        <motion.div
          className="relative z-10 text-center px-4"
          variants={fadeUp}
          initial="hidden"
          animate="visible"
        >
          <h1 className="text-3xl sm:text-6xl font-semibold mb-3 just-me-again-down-here-regular">
            {serviceData?.data?.header?.title}
          </h1>
          <p className="text-sm sm:text-base max-w-xl mx-auto text-gray-200">
            {serviceData?.data?.header?.caption}
          </p>
        </motion.div>
      </section>

      {/* ===== Service Overview ===== */}
      <section className="bg-[#FFF6F2] py-20 px-6 sm:px-12 lg:px-24">
        <motion.div
          className="max-w-4xl mx-auto text-center mb-16"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <h2 className="text-2xl sm:text-3xl font-semibold text-[#AC3F1E] mb-4">What We Offer</h2>
          <p className="text-gray-700 text-sm sm:text-base leading-relaxed">
            {serviceData?.data?.whatWeOffer?.caption}
          </p>
        </motion.div>

        {/* === Service Cards === */}
        <motion.div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10 max-w-6xl mx-auto"
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {[
            {
              icon: <WrenchIcon className="w-10 h-10 text-[#AC3F1E]" />,
              title: serviceData?.data?.whatWeOffer?.columns[0]?.title,
              desc: serviceData?.data?.whatWeOffer?.columns[0]?.text,
            },
            {
              icon: <PenTool className="w-10 h-10 text-[#AC3F1E]" />,
              title: serviceData?.data?.whatWeOffer?.columns[1]?.title,
              desc: serviceData?.data?.whatWeOffer?.columns[1]?.text,
            },
            {
              icon: <Package className="w-10 h-10 text-[#AC3F1E]" />,
              title: serviceData?.data?.whatWeOffer?.columns[2]?.title,
              desc: serviceData?.data?.whatWeOffer?.columns[2]?.text,
            },
          ].map((service, index) => (
            <motion.div
              key={index}
              variants={fadeUp}
              className="bg-white rounded-lg shadow-md hover:shadow-lg transition p-8 flex flex-col items-start"
            >
              <div className="mb-4">{service.icon}</div>
              <h3 className="text-lg font-semibold mb-2 text-[#AC3F1E]">{service.title}</h3>
              <p className="text-sm text-gray-700 leading-relaxed">{service.desc}</p>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* ===== The Process ===== */}
      <section className="bg-white py-20 px-6 sm:px-12 lg:px-24">
        <motion.div
          className="max-w-4xl mx-auto text-center mb-16"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <h2 className="text-2xl sm:text-3xl font-semibold text-[#AC3F1E] mb-4">Our Process</h2>
          <p className="text-gray-700 text-sm sm:text-base leading-relaxed">
            {serviceData?.data?.ourProcess?.caption}
          </p>
        </motion.div>

        <motion.div
          className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-10"
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {[
            {
              icon: <PenTool className="text-[#AC3F1E] w-10 h-10" />,
              step: '01',
              title: serviceData?.data?.ourProcess?.columns[0]?.title,
              desc: serviceData?.data?.ourProcess?.columns[0]?.text,
            },
            {
              icon: <WrenchIcon className="text-[#AC3F1E] w-10 h-10" />,
              step: '02',
              title: serviceData?.data?.ourProcess?.columns[1]?.title,
              desc: serviceData?.data?.ourProcess?.columns[1]?.text,
            },
            {
              icon: <Clock className="text-[#AC3F1E] w-10 h-10" />,
              step: '03',
              title: serviceData?.data?.ourProcess?.columns[2]?.title,
              desc: serviceData?.data?.ourProcess?.columns[2]?.text,
            },
          ].map((phase, i) => (
            <motion.div
              key={i}
              variants={fadeUp}
              className="flex-1 text-center bg-[#FFF6F2] p-8 rounded-lg shadow-md hover:shadow-lg transition"
            >
              <div className="flex flex-col items-center mb-4">
                {phase.icon}
                <span className="mt-2 text-xs tracking-wider font-semibold text-gray-500">
                  {phase.step}
                </span>
              </div>
              <h3 className="text-lg font-semibold text-[#AC3F1E] mb-2">{phase.title}</h3>
              <p className="text-sm text-gray-700 leading-relaxed">{phase.desc}</p>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* ===== CTA Section ===== */}
      <section className="relative bg-[#AC3F1E] text-white py-20 px-6 sm:px-12 lg:px-24 text-center">
        <motion.div
          className="max-w-3xl mx-auto"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <h2 className="text-2xl sm:text-3xl font-semibold mb-4">
            {serviceData?.data?.appointment?.title}
          </h2>
          <p className="text-sm sm:text-base text-gray-100 mb-8">
            {serviceData?.data?.appointment?.caption}
          </p>
          <motion.a
            href={serviceData?.data?.appointment?.buttonUrl}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="inline-block bg-white text-[#AC3F1E] uppercase tracking-wide px-8 py-3 font-semibold text-sm"
          >
            {serviceData?.data?.appointment?.buttonLabel}
          </motion.a>
        </motion.div>
      </section>
    </WebLayout>
  );
}
